import React, { useEffect, useState } from "react";
import { Clock3, Zap, AlertTriangle } from "lucide-react";

function useTick(ms = 30000) {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

function fmt(ms) {
  if (ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${Math.max(1, m)}m`;
}

const TONE = {
  wait: { bg: "rgba(125,169,255,.12)", bd: "rgba(125,169,255,.45)", fg: "#3B63A8", ic: "#5B86D6" },
  act:  { bg: "rgba(0,85,255,.07)",   bd: "rgba(0,85,255,.30)",   fg: "#0044CC", ic: "#0055FF" },
};

/**
 * One live-ticking decision clock. For an introducer it reads from the masked `case_sla`
 * (never names a desk); for a desk it reads from its own lane `sla` as fair warning before any
 * auto-pull. Returns null when there's nothing on the clock (offered / funded / declined).
 */
export function SlaCountdown({ sla, variant = "introducer", step, testId = "case-sla-countdown" }) {
  useTick(30000);
  if (!sla || !sla.state) return null;
  if (sla.state === "waiting_client") return <Waiting sla={sla} variant={variant} step={step} testId={testId} />;
  const dueIso = sla.due_at || sla.pull_at;
  if (!dueIso) return null;
  const left = fmt(new Date(dueIso).getTime() - Date.now());
  const state = sla.state;

  let tone = "wait", Icon = Clock3, head = "", sub = "";
  if (variant === "lender") {
    if (state === "overdue") {
      tone = "act"; Icon = AlertTriangle;
      head = "Decision due now";
      sub = sla.auto_pull
        ? "This case may be reassigned at any time. A decline is a fine answer — the silence is what loses the panel seat."
        : "The introducer's client is waiting. Give it a decision now, even a decline.";
    } else if (state === "chasing") {
      tone = "act"; Icon = Zap;
      head = !sla.auto_pull ? "Decision due now — Connexis is chasing"
        : left ? `Decision due now — ${left} before it may be reassigned` : "Decision due now";
      sub = "Mark it in review, ask for anything you need, or decline. Acting resets the clock.";
    } else {
      tone = "wait"; Icon = Clock3;
      head = left ? `Respond within ${left}` : "Awaiting your decision";
      sub = "Acting on the case — a note, a document request or a decision — resets the clock.";
    }
  } else {
    if (state === "overdue") {
      tone = "act"; Icon = AlertTriangle;
      head = sla.auto_pull ? "Moving it to another desk now" : "We're pushing hard for a decision";
      sub = sla.auto_pull
        ? "The desk went quiet past the deadline, so we're re-placing it. Nobody has passed on the deal."
        : "The desk is past its decision deadline — we're on it for you.";
    } else if (state === "chasing") {
      tone = "act"; Icon = Zap;
      head = sla.auto_pull && left ? `We're chasing the desk now — ${left} left` : "We're chasing the desk for a decision";
      sub = sla.auto_pull
        ? "If it stays quiet we'll move it to another desk. You won't have to ask."
        : "We keep on at the desk until it answers. You won't have to ask.";
    } else {
      tone = "wait"; Icon = Clock3;
      head = left ? `A decision is expected within ${left}` : "A decision is due shortly";
      sub = sla.auto_pull
        ? "If the desk goes quiet we'll chase it, then move it to another desk — automatically."
        : "If the desk goes quiet we'll chase it for you — automatically.";
    }
  }

  return <Box tone={tone} Icon={Icon} head={head} sub={sub} testId={testId} />;
}

const LENDER_WAIT = {
  no_contact: ["Right now: trying to reach the client — your clock is paused", "Keep trying them by phone, text and email. The clock stays paused"],
  callback: ["Right now: call back booked — your clock is paused", "The clock stays paused until the call"],
  valuation: ["Right now: waiting on valuation or searches — your clock is paused", "The clock stays paused while the results come back"],
};

function Waiting({ sla, variant, step, testId }) {
  const until = sla.resumes_at ? new Date(sla.resumes_at).toLocaleString("en-GB", { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: "Europe/London" }) : null;
  const [h, s] = LENDER_WAIT[step] || ["Waiting on the client — your clock is paused",
    "Your message, question or document request is with the client. The clock stays paused until they reply"];
  const head = variant === "lender" ? h : "The desk is waiting to hear back from your client";
  const sub = variant === "lender"
    ? `${s}${until ? ` — until ${until} at the latest` : ""}.`
    : "The desk has been in touch with them, and as soon as they reply it lands on the case.";
  return <Box tone="wait" Icon={Clock3} head={head} sub={sub} testId={testId} />;
}

function Box({ tone, Icon, head, sub, testId }) {
  const t = TONE[tone];
  return (
    <div data-testid={testId}
         style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14,
                  padding: "10px 12px", background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 12 }}>
      <Icon size={16} strokeWidth={2.2} color={t.ic} style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.fg, letterSpacing: "-0.01em" }}
             data-testid={`${testId}-head`}>{head}</div>
        <div style={{ marginTop: 2, fontSize: 12, lineHeight: 1.45, color: "#475569" }}>{sub}</div>
      </div>
    </div>
  );
}
